// ─── GMC Portfolio Pathway — Evidence Validity Rules ─────────────────────────
// Validity periods for CESR evidence types.
// validityYears: how long the evidence remains current
// renewalLeadWeeks: how far ahead to begin renewal process (null = no renewal needed)

export const EVIDENCE_VALIDITY = {
  'ALS': {
    validityYears: 4, renewalLeadWeeks: 12,
    body: 'Resuscitation Council UK',
    renewalUrl: 'https://www.resus.org.uk/training-and-resources/als-course-finder',
    note: 'Must be current at date of GMC submission',
  },
  'APLS': {
    validityYears: 4, renewalLeadWeeks: 12,
    body: 'ALSG',
    renewalUrl: 'https://www.alsg.org/courses/apls',
    note: 'Must be current at date of GMC submission',
  },
  'EPALS': {
    validityYears: 4, renewalLeadWeeks: 12,
    body: 'ALSG',
    renewalUrl: 'https://www.alsg.org/courses/epals',
    note: 'Must be current at date of GMC submission',
  },
  'ATLS': {
    validityYears: 3, renewalLeadWeeks: 16,
    body: 'RCS England',
    renewalUrl: 'https://www.rcseng.ac.uk/education-and-exams/courses/atls',
    note: 'Must be current at date of GMC submission',
  },
  'ILS': {
    validityYears: 1, renewalLeadWeeks: 8,
    body: 'Resuscitation Council UK',
    renewalUrl: 'https://www.resus.org.uk',
    note: 'Annual renewal required',
  },
  'Safeguarding Children Level 3': {
    validityYears: 3, renewalLeadWeeks: 12,
    body: 'NHS England',
    renewalUrl: 'https://www.e-lfh.org.uk/programmes/safeguarding-children',
    note: 'Level 3 required for all anaesthetists and emergency physicians',
  },
  'Safeguarding Adults Level 3': {
    validityYears: 3, renewalLeadWeeks: 12,
    body: 'NHS England',
    renewalUrl: 'https://www.e-lfh.org.uk/programmes/safeguarding-adults',
    note: 'Must be current at submission',
  },
  'Mental Capacity Act Training': {
    validityYears: 3, renewalLeadWeeks: 12,
    body: 'NHS England',
    renewalUrl: 'https://www.e-lfh.org.uk',
    note: 'MCA and DoLS training — 3 year validity',
  },
  'DBS': {
    validityYears: 3, renewalLeadWeeks: 16,
    body: 'Disclosure and Barring Service',
    renewalUrl: 'https://www.gov.uk/request-copy-criminal-record',
    note: 'Enhanced DBS required. Or register with DBS Update Service.',
  },
  'GMC Registration': {
    validityYears: 1, renewalLeadWeeks: 8,
    body: 'General Medical Council',
    renewalUrl: 'https://www.gmc-uk.org/registration-and-licensing/renew-your-registration',
    note: 'Annual renewal. Must be current with licence to practise at submission.',
  },
  'GCP': {
    validityYears: 2, renewalLeadWeeks: 8,
    body: 'NIHR / local R&D',
    renewalUrl: 'https://www.nihr.ac.uk/health-and-care-professionals/learning-and-support/good-clinical-practice.htm',
    note: 'Only required if involved in clinical trials',
  },
  'Annual Appraisal': {
    validityYears: 1, renewalLeadWeeks: 8,
    body: 'NHS England / Responsible Officer',
    renewalUrl: null,
    note: 'GMC requires 5 consecutive annual appraisals',
  },
  'Medical Indemnity': {
    validityYears: 1, renewalLeadWeeks: 6,
    body: 'MDU / MPS / MDDUS / NHS',
    renewalUrl: null,
    note: 'Continuous cover required. Must be current at submission.',
  },
  'CPD Record': {
    validityYears: 1, renewalLeadWeeks: 4,
    body: 'Royal College of Anaesthetists',
    renewalUrl: 'https://www.rcoa.ac.uk/training-careers/lifelong-learning',
    note: 'Minimum 50 CPD credits per year',
  },
  'Logbook': {
    validityYears: 5, renewalLeadWeeks: 52,
    body: 'GMC Portfolio Pathway policy',
    renewalUrl: 'https://www.gmc-uk.org/registration-and-licensing/join-our-registers/registration-applications/portfolio-pathway',
    note: 'GMC requires primary clinical evidence from within the last 5 years',
  },
  'WBAs': {
    validityYears: 5, renewalLeadWeeks: 52,
    body: 'GMC Portfolio Pathway policy',
    renewalUrl: null,
    note: 'Workplace-based assessments must be from within the last 5 years',
  },
  'MSF': {
    validityYears: 5, renewalLeadWeeks: 26,
    body: 'GMC / Revalidation cycle',
    renewalUrl: null,
    note: 'Multi-source feedback should be within the last 5 years and ideally within the current revalidation cycle',
  },
  'AuditReport': {
    validityYears: 5, renewalLeadWeeks: 52,
    body: 'GMC Portfolio Pathway policy',
    renewalUrl: null,
    note: 'Audit data and QI projects should be from within the last 5 years',
  },
  'ClinicalEvidence': {
    validityYears: 5, renewalLeadWeeks: 52,
    body: 'GMC Portfolio Pathway policy',
    renewalUrl: null,
    note: 'Clinical evidence must be from within the last 5 years',
  },
  'SupervisorLetter': {
    validityYears: 2, renewalLeadWeeks: 26,
    body: 'GMC best practice',
    renewalUrl: null,
    note: 'Supervisor letters should ideally be within 2 years and must reflect current practice',
  },
  'TeachingLog': {
    validityYears: 5, renewalLeadWeeks: 52,
    body: 'GMC Portfolio Pathway policy',
    renewalUrl: null,
    note: 'Teaching evidence should be from within the last 5 years',
  },
  'PatientFeedback': {
    validityYears: 5, renewalLeadWeeks: 26,
    body: 'GMC / Revalidation cycle',
    renewalUrl: null,
    note: 'Patient feedback must be within the last 5 years',
  },
  'Publication': {
    validityYears: 10, renewalLeadWeeks: null,
    body: 'GMC Portfolio Pathway policy',
    renewalUrl: null,
    note: 'Publications do not expire but recent publications carry significantly more weight',
  },
  'Appraisal': {
    validityYears: 1, renewalLeadWeeks: 8,
    body: 'NHS England / GMC revalidation',
    renewalUrl: null,
    note: '5 consecutive years required. Annual cycle.',
  },
};

const VALIDITY_KEYS = Object.keys(EVIDENCE_VALIDITY);

/**
 * Try to match a docType / item label to a key in EVIDENCE_VALIDITY.
 * Returns the matched key string or null. Pure lookup — no API call.
 */
export function matchValidityRuleSync(docType, label) {
  // 1. Exact docType match
  if (docType && EVIDENCE_VALIDITY[docType]) return docType;

  // 2. Case-insensitive substring — label contains key or key contains label
  const lbl = (label || '').toLowerCase();
  const dt  = (docType || '').toLowerCase();
  for (const key of VALIDITY_KEYS) {
    const k = key.toLowerCase();
    if (lbl.includes(k) || dt.includes(k) || k.includes(dt)) return key;
  }
  return null;
}

/**
 * Add years to a YYYY-MM-DD date string. Returns a YYYY-MM-DD string.
 */
export function addYearsToDate(dateStr, years) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().split('T')[0];
}

const GMC_CURRENCY_TOOLTIP =
  'GMC Portfolio Pathway policy: primary clinical evidence must generally be ' +
  'from the last 5 years of practice at the point of submission.';

/**
 * Given an evidence date (YYYY-MM-DD), return currency badge info.
 * Returns null if no date provided.
 */
export function getEvidenceAgeBadge(evidenceDate) {
  if (!evidenceDate) return null;
  const now  = new Date();
  const date = new Date(evidenceDate + 'T00:00:00');
  const ageYears = (now - date) / (365.25 * 24 * 60 * 60 * 1000);
  const yearsRemaining = 5 - ageYears;

  if (ageYears < 3) {
    return {
      level: 'current',
      color: '#10b981',
      bg: 'rgba(16,185,129,.1)',
      brd: 'rgba(16,185,129,.25)',
      label: 'Current',
      tooltip: `Evidence is ${ageYears.toFixed(1)} years old — well within the GMC 5-year window. ${GMC_CURRENCY_TOOLTIP}`,
      ageYears,
      yearsRemaining,
    };
  }
  if (ageYears < 4) {
    return {
      level: 'ageing',
      color: '#f59e0b',
      bg: 'rgba(245,158,11,.1)',
      brd: 'rgba(245,158,11,.25)',
      label: 'Ageing — plan to refresh',
      tooltip: `Evidence is ${ageYears.toFixed(1)} years old — ${yearsRemaining.toFixed(1)} years left in GMC window. Plan to gather supplementary recent evidence. ${GMC_CURRENCY_TOOLTIP}`,
      ageYears,
      yearsRemaining,
    };
  }
  if (ageYears < 5) {
    return {
      level: 'approaching',
      color: '#f97316',
      bg: 'rgba(249,115,22,.1)',
      brd: 'rgba(249,115,22,.25)',
      label: 'Approaching GMC 5yr limit',
      tooltip: `Evidence is ${ageYears.toFixed(1)} years old — only ${(yearsRemaining * 12).toFixed(0)} months left in GMC window. Fresh supporting evidence needed urgently. ${GMC_CURRENCY_TOOLTIP}`,
      ageYears,
      yearsRemaining,
    };
  }
  return {
    level: 'outside',
    color: '#ef4444',
    bg: 'rgba(239,68,68,.1)',
    brd: 'rgba(239,68,68,.25)',
    label: 'Outside GMC window',
    tooltip: `Evidence is ${ageYears.toFixed(1)} years old — outside the GMC 5-year window. This must be supported by recent primary evidence before submission. ${GMC_CURRENCY_TOOLTIP}`,
    ageYears,
    yearsRemaining,
  };
}
